/**
 * CategoryConfigPanel Component
 *
 * Configuration panel for Category field type with 3 collapsible sections:
 * - Category Details
 * - Product Listing
 * - Product Details
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CategoryConfigPanelProps } from '../Category/types';
import {
  CATEGORY_DEFAULT_CONFIG,
  CategoryFieldConfig,
  CategoryStyle,
  CategoryImageDisplay,
  ProductsLoading,
  DescriptionStyle,
  CategoryTextAlignment,
} from '../../config/fieldDefaults';
import TextField from 'Components/DesignSystem/TextField/TextField';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Radio from 'Components/DesignSystem/Radio/Radio';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { useDebouncedInput } from '../../hooks/useDebouncedInput';
import { useIconUpload } from '../../hooks/useIconUpload';
import CollapsibleSection from './CollapsibleSection';
import { useUIModelApi } from '../../contexts/UIModelApiContext';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import { imageIconUploadAPI } from '../../services/ApiHelper';
import InfoTooltip from 'Components/DesignSystem/Tooltip/InfoTooltip';

const DEBOUNCE_DELAY = 500;

// Dropdown Options
const IMAGE_DISPLAY_OPTIONS = [
  { value: 'Fit', label: 'Fit' },
  { value: 'Fill', label: 'Fill' },
  { value: 'Stretch', label: 'Stretch' },
];

const PRODUCTS_PER_ROW_OPTIONS = [
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5' },
  { value: '6', label: '6' },
];

const SCROLL_ROWS_OPTIONS = [
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5' },
  { value: '6', label: '6' },
];


const DESCRIPTION_STYLE_OPTIONS = [
  { value: 'Partly', label: 'Partly' },
  // { value: 'Full', label: 'Full' },
  { value: 'Collapsed', label: 'Collapsed' },
];

const TEXT_ALIGNMENT_OPTIONS = [
  { value: 'Left', label: 'Left' },
  { value: 'Centre', label: 'Centre' },
  { value: 'Right', label: 'Right' },
];

export const CategoryConfigPanel = ({
  config,
  onChange,
  categoryId,
}: CategoryConfigPanelProps) => {
  const [localConfig, setLocalConfig] = useState<CategoryFieldConfig>(() => {
    const defaults = { label: '', ...CATEGORY_DEFAULT_CONFIG };
    return { ...defaults, ...config };
  });
  const [uploadMethod, setUploadMethod] = useState<'file' | 'url'>('file');
  const [isUploading, setIsUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');

  const { config: apiConfig } = useUIModelApi();
  const { state } = useUIModelState();
  useEffect(() => {
    const defaults = { label: '', ...CATEGORY_DEFAULT_CONFIG };
    setLocalConfig({ ...defaults, ...config });
  }, [config]);

  const handleConfigChange = useCallback(
    <K extends keyof CategoryFieldConfig>(key: K, value: CategoryFieldConfig[K]) => {
      const newConfig = { ...localConfig, [key]: value };
      setLocalConfig(newConfig);
      onChange(newConfig);
    },
    [localConfig, onChange]
  );

  // Debounced inputs
  const labelInput = useDebouncedInput(
    localConfig.label,
    (value) => handleConfigChange('label', value),
    DEBOUNCE_DELAY
  );

  const titleTextInput = useDebouncedInput(
    localConfig.titleText,
    (value) => handleConfigChange('titleText', value),
    DEBOUNCE_DELAY
  );

  const descriptionRef = useRef<HTMLDivElement>(null);

  // Sync contentEditable div with localConfig.descriptionText.
  // showCategoryDescription is a dependency so the effect re-runs when the
  // section is toggled back on: the div remounts empty, and the saved text
  // must be re-applied (descriptionText itself is unchanged, so it alone
  // would not trigger this sync).
  useEffect(() => {
    if (descriptionRef.current && descriptionRef.current.innerHTML !== localConfig.descriptionText) {
      descriptionRef.current.innerHTML = localConfig.descriptionText || '';
    }
  }, [localConfig.descriptionText, localConfig.showCategoryDescription]);

  const inStockLabelInput = useDebouncedInput(
    localConfig.inStockLabel,
    (value) => handleConfigChange('inStockLabel', value),
    DEBOUNCE_DELAY
  );

  const backOrderLabelInput = useDebouncedInput(
    localConfig.backOrderLabel,
    (value) => handleConfigChange('backOrderLabel', value),
    DEBOUNCE_DELAY
  );

  const buttonLabelInput = useDebouncedInput(
    localConfig.buttonLabel,
    (value) => handleConfigChange('buttonLabel', value),
    DEBOUNCE_DELAY
  );

  const inStockTextInput = useDebouncedInput(
    localConfig.inStockText,
    (value) => handleConfigChange('inStockText', value),
    DEBOUNCE_DELAY
  );

  // Category image upload
  const categoryImageUpload = useIconUpload(localConfig.categoryImageUrl || '');

  const handleCategoryImageFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Use hook for local preview
    categoryImageUpload.handleUpload(event);

    // Upload to API
    const configureId = apiConfig.configureId;
    const modelId = state.model?.id;
    if (configureId && modelId) {
      setIsUploading(true);
      try {
        const response = await imageIconUploadAPI(configureId, modelId, file, 'category');
        const uploadedUrl = response?.data?.imageUrl || response?.imageUrl;
        if (uploadedUrl) {
          handleConfigChange('categoryImageUrl', uploadedUrl);
        }
      } catch (error) {
        console.error('Failed to upload category image:', error);
      } finally {
        setIsUploading(false);
      }
    }
  }, [apiConfig, state.model?.id, categoryImageUpload, handleConfigChange]);

  const handleCategoryImageUrlSubmit = useCallback(() => {
    if (urlInput.trim()) {
      handleConfigChange('categoryImageUrl', urlInput.trim());
    }
  }, [urlInput, handleConfigChange]);

  const handleCategoryImageRemove = useCallback(() => {
    categoryImageUpload.handleRemove();
    handleConfigChange('categoryImageUrl', '');
    setUrlInput('');
  }, [categoryImageUpload, handleConfigChange]);

  // Button icon upload — propagate local data-URL immediately so preview reflects the icon
  const buttonIconUpload = useIconUpload(
    localConfig.buttonIconUrl || '',
    (url) => handleConfigChange('buttonIconUrl', url),
  );

  const handleButtonIconFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    buttonIconUpload.handleUpload(event);

    const configureId = apiConfig.configureId;
    const modelId = state.model?.id;
    if (configureId && modelId) {
      setIsUploading(true);
      try {
        const response = await imageIconUploadAPI(configureId, modelId, file, 'buttonIcon');
        const uploadedUrl = response?.data?.imageUrl || response?.imageUrl;
        if (uploadedUrl) {
          handleConfigChange('buttonIconUrl', uploadedUrl);
        }
      } catch (error) {
        console.error('Failed to upload button icon:', error);
      } finally {
        setIsUploading(false);
      }
    }
  }, [apiConfig, state.model?.id, buttonIconUpload, handleConfigChange]);

  const handleButtonIconRemove = useCallback(() => {
    buttonIconUpload.handleRemove();
    handleConfigChange('buttonIconUrl', '');
  }, [buttonIconUpload, handleConfigChange]);

  return (
    <div className="tw-space-y-4">
      <div>
        {/* Category Details Section */}
        <CollapsibleSection title="Category Details" defaultOpen>
          <div className="tw-space-y-4">
            {/* Label */}
            <TextField
              label="Label"
              labelSize="sm"
              required
              value={labelInput.localValue}
              onChange={labelInput.handleChange}
              placeholder="Category name"
            />

            {/* Show Title */}
            <Checkbox
              label="Show Title"
              checked={localConfig.showTitle}
              onChange={(checked) => handleConfigChange('showTitle', checked)}
            />

            {/* Title Text */}
            {localConfig.showTitle && (
              <TextField
                label="Title Text"
                labelSize="sm"
                value={titleTextInput.localValue}
                onChange={titleTextInput.handleChange}
                placeholder="Category title"
              />
            )}

            {/* Show Category Description */}
            <Checkbox
              label="Show Category Description"
              checked={localConfig.showCategoryDescription}
              onChange={(checked) => handleConfigChange('showCategoryDescription', checked)}
            />

            {/* Description Text */}
            {localConfig.showCategoryDescription && (
              <div className="tw-space-y-2">
                <FieldTitle label="Description Text" size="sm" />
                <div className="tw-flex tw-gap-1">
                  <button data-testid="configpanel-categoryconfigpanel-button-1"
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); document.execCommand('bold', false, ''); }}
                    className="tw-w-8 tw-h-8 tw-border tw-border-edge tw-rounded tw-flex tw-items-center tw-justify-center tw-bg-surface hover:tw-bg-surface-hover active:tw-bg-surface-sunken"
                  >
                    <span className="tw-font-extrabold tw-text-sm tw-text-ink">B</span>
                  </button>
                  <button data-testid="configpanel-categoryconfigpanel-button-2"
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); document.execCommand('italic', false, ''); }}
                    className="tw-w-8 tw-h-8 tw-border tw-border-edge tw-rounded tw-flex tw-items-center tw-justify-center tw-bg-surface hover:tw-bg-surface-hover active:tw-bg-surface-sunken"
                  >
                    <span className="tw-italic tw-text-sm tw-font-extrabold tw-text-ink">I</span>
                  </button>
                </div>
                <div
                  ref={descriptionRef}
                  contentEditable
                  suppressContentEditableWarning
                  onInput={(e) => handleConfigChange('descriptionText', e.currentTarget.innerHTML)}
                  onBlur={(e) => handleConfigChange('descriptionText', e.currentTarget.innerHTML)}
                  className="tw-w-full tw-border tw-text-sm tw-text-ink tw-font-medium tw-border-edge tw-rounded tw-px-3 tw-py-2 tw-min-h-[80px] tw-bg-surface focus:tw-outline-none focus:tw-border-accent focus:tw-ring-1 focus:tw-ring-accent"
                  style={{ wordWrap: 'break-word' }}
                />
              </div>
            )}

            {/* Show Thumbnail */}
            <Checkbox
              label="Show Thumbnail"
              checked={localConfig.showThumbnail}
              onChange={(checked) => handleConfigChange('showThumbnail', checked)}
            />

            {/* Image Display */}
            {localConfig.showThumbnail && (
              <Dropdown
                label="Image Display"
                labelSize="sm"
                value={localConfig.imageDisplay}
                options={IMAGE_DISPLAY_OPTIONS}
                onChange={(value) => handleConfigChange('imageDisplay', value as CategoryImageDisplay)}
              />
            )}

            {/* Category Image Upload */}
            {localConfig.showThumbnail && (
              <div className="tw-space-y-2">
                <FieldTitle label="Category Image" size="sm" />
                {/* Upload Method Toggle */}
                <div className="tw-flex tw-gap-1">
                  <button data-testid="configpanel-categoryconfigpanel-button-3"
                    className={`tw-px-3 tw-py-1 tw-text-xs tw-rounded tw-border tw-transition-colors ${uploadMethod === 'file'
                        ? 'tw-bg-accent-bold tw-text-ink-inverse tw-border-accent-bold'
                        : 'tw-bg-surface tw-text-ink tw-border-edge hover:tw-border-edge-strong'
                      }`}
                    onClick={() => setUploadMethod('file')}
                  >
                    Upload File
                  </button>
                  <button data-testid="configpanel-categoryconfigpanel-button-4"
                    className={`tw-px-3 tw-py-1 tw-text-xs tw-rounded tw-border tw-transition-colors ${uploadMethod === 'url'
                        ? 'tw-bg-accent-bold tw-text-ink-inverse tw-border-accent-bold'
                        : 'tw-bg-surface tw-text-ink tw-border-edge hover:tw-border-edge-strong'
                      }`}
                    onClick={() => setUploadMethod('url')}
                  >
                    Enter URL
                  </button>
                </div>

                {uploadMethod === 'file' ? (
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <label className="tw-cursor-pointer tw-px-3 tw-py-1 tw-text-sm tw-border tw-border-edge tw-text-accent tw-rounded hover:tw-bg-accent-tint hover:tw-border-accent-hover tw-transition-colors">
                      {isUploading ? 'Uploading...' : 'Choose File'}
                      <input data-testid="configpanel-categoryconfigpanel-input-5"
                        ref={categoryImageUpload.inputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={handleCategoryImageFileUpload}
                        className="tw-hidden"
                        disabled={isUploading}
                      />
                    </label>
                  </div>
                ) : (
                  <div className="tw-flex tw-gap-2">
                    <TextField
                      label=""
                      value={urlInput}
                      onChange={(value) => setUrlInput(value)}
                      placeholder="https://example.com/image.png"
                    />
                    <button data-testid="configpanel-categoryconfigpanel-button-6"
                      className="tw-px-3 tw-py-1 tw-text-sm tw-border tw-border-edge tw-text-accent tw-rounded hover:tw-bg-accent-tint hover:tw-border-accent-hover tw-transition-colors tw-flex-shrink-0 tw-self-end"
                      onClick={handleCategoryImageUrlSubmit}
                    >
                      Apply
                    </button>
                  </div>
                )}

                <p className="tw-text-[10px] tw-text-ink-secondary">
                  Max file size: 5MB. Supported: JPEG, PNG, WebP, GIF
                </p>

                {/* Image Preview */}
                {(localConfig.categoryImageUrl || categoryImageUpload.previewUrl) && (
                  <div className="tw-relative tw-group tw-inline-block">
                    <img
                      src={localConfig.categoryImageUrl || categoryImageUpload.previewUrl}
                      alt="Category thumbnail"
                      className="tw-h-16 tw-w-auto tw-max-w-[160px] tw-object-contain tw-border tw-border-edge tw-rounded"
                    />
                    <button data-testid="configpanel-categoryconfigpanel-button-7"
                      onClick={handleCategoryImageRemove}
                      className="tw-absolute tw-top-0 tw-right-0 tw-transform tw-translate-x-1/2 -tw-translate-y-1/2 tw-bg-tone-danger-bold tw-text-ink-inverse tw-rounded-full tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center tw-text-xs tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity"
                      title="Remove image"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Show In Stock Filter */}
            <Checkbox
              label="Show 'In Stock'"
              checked={localConfig.showInStock}
              onChange={(checked) => handleConfigChange('showInStock', checked)}
            />

            {localConfig.showInStock && (
              <>
                <TextField
                  label="Filter Label Text"
                  labelSize="sm"
                  value={inStockTextInput.localValue}
                  onChange={inStockTextInput.handleChange}
                  placeholder="Show In Stock only"
                />
                <Checkbox
                  label="Select by default"
                  checked={localConfig.selectByDefault}
                  onChange={(checked) => handleConfigChange('selectByDefault', checked)}
                />
              </>
            )}

            {/* Hide If No Products */}
            {/* items-start matches the Checkbox's own box alignment; no outer gap
                because the Checkbox already renders a gap-3 after its box */}
            <div className="tw-flex tw-items-start">
              <Checkbox
                checked={localConfig.hideIfNoProducts}
                onChange={(checked) => handleConfigChange('hideIfNoProducts', checked)}
              />
              <Typography variant="body-primary" className="tw-text-ink">
                Hide category if 'no' products are{' '}
                <span className="tw-inline-flex tw-items-center tw-gap-1">
                  available
                  <InfoTooltip
                    label="About hiding a category with no products"
                    data-testid="category-config-hide-if-no-products-info"
                    message="When enabled, automatically hides the sections (Page/SubModel/Category) if no compatible products are available"
                  />
                </span>
              </Typography>
            </div>
          </div>
        </CollapsibleSection>

        {/* Product Listing Section */}
        <CollapsibleSection title="Product Listing" defaultOpen>
          <div className="tw-space-y-4">
            {/* Style */}
            <FieldTitle label="Display Style" size="sm" />
            <div className="tw-space-y-2">
              <Radio
                label="Grid"
                value="Grid"
                checked={localConfig.style === 'Grid'}
                onChange={() => handleConfigChange('style', 'Grid' as CategoryStyle)}
                name="displayStyle"
              />
              <Radio
                label="List"
                value="List"
                checked={localConfig.style === 'List'}
                onChange={() => handleConfigChange('style', 'List' as CategoryStyle)}
                name="displayStyle"
              />
            </div>

            {/* Products Per Row - only relevant for Grid style */}
            {localConfig.style === 'Grid' && (
              <div>
                <div className="tw-flex tw-items-center tw-gap-1 tw-mb-1">
                  <FieldTitle label="Products Per Row" size="sm" />
                  <InfoTooltip
                    label="About Products Per Row"
                    data-testid="category-config-products-per-row-info"
                    message="Maximum products per row. Layout may adjust to a lower number based on screen size"
                    placement="bottom"
                  />
                </div>
                <Dropdown
                  label=""
                  labelSize="sm"
                  value={localConfig.productsPerRow}
                  options={PRODUCTS_PER_ROW_OPTIONS}
                  onChange={(value) => handleConfigChange('productsPerRow', String(value))}
                />
              </div>
            )}

            {/* Products Loading */}
            <FieldTitle label="Products Loading" size="sm" />
            <div className="tw-space-y-2">
              <div>
                <div className="tw-flex tw-items-center tw-gap-1">
                  <Radio
                    label="Infinite Scroll"
                    value="Infinite Scroll"
                    checked={localConfig.productsLoading === 'Infinite Scroll'}
                    onChange={() => handleConfigChange('productsLoading', 'Infinite Scroll' as ProductsLoading)}
                    name="productsLoading"
                  />
                  <InfoTooltip label="About Infinite Scroll" data-testid="category-config-infinite-scroll-info" message="Products load automatically as the customer scrolls down the page. No button needed — new products appear as they reach the bottom." />
                </div>
                {localConfig.productsLoading === 'Infinite Scroll' && (
                  <div className="tw-ml-6 tw-mt-2">
                    <TextField
                      label="Load products per scroll"
                      labelSize="sm"
                      value={localConfig.showButtonAfterProducts}
                      onChange={(value) => {
                        const intOnly = value.replace(/\D/g, '');
                        handleConfigChange('showButtonAfterProducts', intOnly);
                      }}
                      placeholder="Enter a number"
                    />
                  </div>
                )}
              </div>
              <div>
                <div className="tw-flex tw-items-center tw-gap-1">
                  <Radio
                    label="Load More Button"
                    value="Load More Button"
                    checked={localConfig.productsLoading === 'Load More Button'}
                    onChange={() => handleConfigChange('productsLoading', 'Load More Button' as ProductsLoading)}
                    name="productsLoading"
                  />
                  <InfoTooltip label="About Load More Button" data-testid="category-config-load-more-info" message="A &apos;Load More&apos; button appears after the initial products. Customers click it to fetch the next batch." />
                </div>
                {localConfig.productsLoading === 'Load More Button' && (
                  <div className="tw-ml-6 tw-mt-2">
                    <TextField
                      label="Show 'Load More' button after 'X' products"
                      labelSize="sm"
                      value={localConfig.showButtonAfterProducts}
                      onChange={(value) => {
                        const intOnly = value.replace(/\D/g, '');
                        handleConfigChange('showButtonAfterProducts', intOnly);
                      }}
                      placeholder="Enter a number"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </CollapsibleSection>

        {/* Product Details Section */}
        <CollapsibleSection title="Product Details" defaultOpen>
          <div className="tw-space-y-4">
            {/* Field order matches runtime Product Details layout:
                Model Number → Product Description → Category → Manufacturer →
                Price → UOM → Availability Status → Button. Remaining options
                (PDP Navigation, Launch In, Text Alignment) follow the button. */}

            {/* Show Model Number */}
            <Checkbox
              label="Show Model Number"
              checked={localConfig.showModelNumber}
              onChange={(checked) => handleConfigChange('showModelNumber', checked)}
            />

            {/* Show Product Description */}
            <Checkbox
              label="Show Product Description"
              checked={localConfig.showProductDescription}
              onChange={(checked) => handleConfigChange('showProductDescription', checked)}
            />

            {/* Description Style */}
            {localConfig.showProductDescription && (
              <Dropdown
                label="Description Style"
                labelSize="sm"
                value={localConfig.descriptionStyle}
                options={DESCRIPTION_STYLE_OPTIONS}
                onChange={(value) => handleConfigChange('descriptionStyle', value as DescriptionStyle)}
              />
            )}

            {/* Show Category */}
            <Checkbox
              label="Show Category"
              checked={localConfig.showCategory}
              onChange={(checked) => handleConfigChange('showCategory', checked)}
            />

            {/* Show Manufacturer */}
            <Checkbox
              label="Show Manufacturer"
              checked={localConfig.showManufacturer}
              onChange={(checked) => handleConfigChange('showManufacturer', checked)}
            />

            {/* Show Price */}
            <Checkbox
              label="Show Price"
              checked={localConfig.showPrice}
              onChange={(checked) => handleConfigChange('showPrice', checked)}
            />

            {/* Show UOM - only visible when Show Price is checked */}
            {localConfig.showPrice && (
              <div className="tw-ml-6 tw-flex tw-items-center tw-gap-1">
                <Checkbox
                  label="Show UOM"
                  checked={localConfig.showUOM}
                  onChange={(checked) => handleConfigChange('showUOM', checked)}
                />
                <InfoTooltip
                  label="About Show UOM"
                  data-testid="category-config-show-uom-info"
                  message="UOM – Unit of Measurement. Displays the unit beside the price for products sold by weight, volume, length, etc."
                  placement="bottom"
                />
              </div>
            )}

            {/* Show Availability Status */}
            <Checkbox
              label="Show Availability Status"
              checked={localConfig.showAvailabilityStatus}
              onChange={(checked) => handleConfigChange('showAvailabilityStatus', checked)}
            />

            {/* Availability Labels */}
            {localConfig.showAvailabilityStatus && (
              <div className="tw-flex tw-gap-3">
                <div className="tw-flex-1">
                  <TextField
                    label="In Stock Label"
                    labelSize="sm"
                    value={inStockLabelInput.localValue}
                    onChange={inStockLabelInput.handleChange}
                    placeholder="In Stock"
                  />
                </div>
                <div className="tw-flex-1">
                  <TextField
                    label="Back Order Label"
                    labelSize="sm"
                    value={backOrderLabelInput.localValue}
                    onChange={backOrderLabelInput.handleChange}
                    placeholder="Back Order"
                  />
                </div>
              </div>
            )}

            {/* Button */}
            <FieldTitle label="Button" size="sm" />

            <Checkbox
              label="Show Text"
              checked={localConfig.showButtonText}
              onChange={(checked) => handleConfigChange('showButtonText', checked)}
            />

            {localConfig.showButtonText && (
              <TextField
                label="Button Label"
                labelSize="sm"
                value={buttonLabelInput.localValue}
                onChange={buttonLabelInput.handleChange}
                placeholder="Add to Cart"
              />
            )}

            <Checkbox
              label="Show Icon"
              checked={localConfig.showButtonIcon}
              onChange={(checked) => handleConfigChange('showButtonIcon', checked)}
            />

            {localConfig.showButtonIcon && (
              <div className="tw-space-y-2">
                {(localConfig.buttonIconUrl || buttonIconUpload.previewUrl) && (
                  <div className="tw-relative tw-group tw-inline-block">
                    <img
                      src={localConfig.buttonIconUrl || buttonIconUpload.previewUrl}
                      alt="Button icon"
                      className="tw-h-10 tw-w-auto tw-max-w-[80px] tw-object-contain tw-border tw-border-edge tw-rounded"
                    />
                    <button data-testid="configpanel-categoryconfigpanel-button-8"
                      onClick={handleButtonIconRemove}
                      className="tw-absolute tw-top-0 tw-right-0 tw-transform tw-translate-x-1/2 -tw-translate-y-1/2 tw-bg-tone-danger-bold tw-text-ink-inverse tw-rounded-full tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center tw-text-xs tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity"
                      title="Remove icon"
                    >
                      ×
                    </button>
                  </div>
                )}
                <div className="tw-flex tw-items-center tw-gap-3">
                  <label className="tw-cursor-pointer tw-px-3 tw-py-1 tw-text-sm tw-border tw-border-edge tw-text-accent tw-rounded hover:tw-bg-accent-tint hover:tw-border-accent-hover tw-transition-colors">
                    {isUploading ? 'Uploading...' : (localConfig.buttonIconUrl ? 'Change Icon' : 'Upload Icon')}
                    <input data-testid="configpanel-categoryconfigpanel-input-9"
                      ref={buttonIconUpload.inputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                      onChange={handleButtonIconFileUpload}
                      className="tw-hidden"
                      disabled={isUploading}
                    />
                  </label>
                  {buttonIconUpload.file?.name && (
                    <span className="tw-text-xs tw-text-ink-secondary tw-truncate tw-max-w-[120px]">{buttonIconUpload.file.name}</span>
                  )}
                </div>
                <p className="tw-text-[10px] tw-text-ink-secondary">
                  Max file size: 5MB. Supported: JPEG, PNG, WebP, GIF, SVG
                </p>
              </div>
            )}

            {/* Remaining options below the button */}

            {/* PDP Navigation */}
            <FieldTitle label="PDP Navigation" size="sm" />

            <div className="tw-space-y-2">
              <Typography variant="sub-title-label" className="tw-text-ink-secondary tw-pl-1">
                Launch On
              </Typography>
              <Checkbox
                label="Title Click"
                checked={localConfig.pdpLaunchOnTitleClick}
                onChange={(checked) => {
                  if (!checked && !localConfig.pdpLaunchOnImageClick) return;
                  handleConfigChange('pdpLaunchOnTitleClick', checked);
                }}
              />
              <Checkbox
                label="Image Click"
                checked={localConfig.pdpLaunchOnImageClick}
                onChange={(checked) => {
                  if (!checked && !localConfig.pdpLaunchOnTitleClick) return;
                  handleConfigChange('pdpLaunchOnImageClick', checked);
                }}
              />
            </div>

            <div className="tw-space-y-2">
              <Typography variant="sub-title-label" className="tw-text-ink-secondary tw-pl-1">
                Launch In
              </Typography>
              <Radio
                label="New Window"
                value="New Window"
                checked={localConfig.pdpLaunchIn === 'New Window'}
                onChange={() => handleConfigChange('pdpLaunchIn', 'New Window')}
                name="pdpLaunchIn"
              />
              <Radio
                label="New Tab"
                value="New Tab"
                checked={localConfig.pdpLaunchIn === 'New Tab'}
                onChange={() => handleConfigChange('pdpLaunchIn', 'New Tab')}
                name="pdpLaunchIn"
              />
            </div>

            {/* Text Alignment */}
            <Dropdown
              label="Product Details Text Alignment"
              labelSize="sm"
              value={localConfig.productDetailsTextAlignment}
              options={TEXT_ALIGNMENT_OPTIONS}
              onChange={(value) => handleConfigChange('productDetailsTextAlignment', value as CategoryTextAlignment)}
            />
          </div>
        </CollapsibleSection>
      </div>
    </div>
  );
};

export default CategoryConfigPanel;
